/* ==========================================================================
   Cherry Pot — Quick View on product cards (added 2026-09-30)

   A round "eye" button on every product card opens the product in a popup
   right where the shopper is (reference: SK Cookware). Tapping the card
   itself still goes to the product page.

   Since 2026-09-30 (owner request) the card's own buttons open the same
   popup too: "Add to cart" / "Choose size" on every product card
   (snippets/cp-card-extras.liquid, theme cards and homepage rails) and
   "Choose size" in the cart drawer's "You may also like" rail. The cart
   rail's single-variant "Add" button still adds directly.

   It reuses the theme's own quick-add popup (quick-add-dialog,
   assets/quick-add.js): same size picker, same Add to cart / Buy it now, and
   it closes itself when the item lands in the cart. We only add:
     - the button on both card types (theme cards and the homepage rails,
       which are not <product-card> and had no quick-add at all),
     - a large swipeable image on phones (stock Horizon shows a 72px thumb),
     - a "View full details" link,
     - Clarity events: quickview_open.
   Styles: assets/cp-quickview.css.

   If anything is missing (popup not on the page, fetch fails) the button
   simply navigates to the product page, so it can never strand a shopper.
   ========================================================================== */
(() => {
  if (window.__cpQuickView) return;
  window.__cpQuickView = true;

  const ICON =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>';

  const CARD_SELECTOR = 'product-card, .cprail-card';
  const SKIP_INSIDE = '.quick-add-modal, .cart-drawer, .search-modal, predictive-search-component, .cart-page';

  /** @type {Map<string, Element>} */
  const cache = new Map();
  document.addEventListener('cart:update', () => cache.clear());

  const productLink = (card) => card.querySelector('a[href*="/products/"]');

  const cardTitle = (card) => {
    const el =
      card.querySelector('.cprail-card__title') ||
      card.querySelector('.product-card__title, .product-title, [class*="title"] a, h3');
    return (el?.textContent || '').trim();
  };

  const addButtons = () => {
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (card.hasAttribute('data-cpqv')) return;
      if (card.closest(SKIP_INSIDE)) return;
      const link = productLink(card);
      if (!link) return;

      const host = card.matches('.cprail-card') ? card : card.querySelector('.card-gallery');
      if (!host) return;

      card.setAttribute('data-cpqv', '');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cpqv-btn';
      btn.setAttribute('aria-label', 'Quick view');
      btn.dataset.url = link.href;
      btn.dataset.title = cardTitle(card);
      btn.innerHTML = ICON;
      host.appendChild(btn);
    });
  };

  const getQuickAddClass = async () => {
    let QA = customElements.get('quick-add-component');
    if (!QA) {
      try {
        await import('@theme/quick-add');
      } catch (e) {
        /* fall through to navigation */
      }
      QA = customElements.get('quick-add-component');
    }
    return QA;
  };

  const addExtras = (content, url) => {
    // Full-details link. On desktop the popup keeps the product-details
    // column, so the link goes at the bottom of it; the wrapper reuses the
    // theme's view-more-details__wrapper class because the popup's CSS hides
    // everything in that column that is not on its allow-list. On phones the
    // theme removes that column, so the link goes at the end of the popup.
    const more = document.createElement('a');
    more.className = 'cpqv-more';
    more.href = url;
    more.textContent = 'View full details';
    const details = content.querySelector('.product-details');
    if (details) {
      const wrap = document.createElement('div');
      wrap.className = 'view-more-details__wrapper cpqv-more-wrap';
      wrap.appendChild(more);
      details.appendChild(wrap);
    } else {
      content.appendChild(more);
    }

    // Dots under the swipeable image (phones)
    const slides = content.querySelector('.product-information__media slideshow-slides');
    const count = slides ? slides.querySelectorAll('slideshow-slide').length : 0;
    if (!slides || count < 2) return;

    const dots = document.createElement('div');
    dots.className = 'cpqv-dots';
    for (let i = 0; i < count; i++) {
      const d = document.createElement('span');
      if (i === 0) d.className = 'is-active';
      dots.appendChild(d);
    }
    content.querySelector('.product-information__media').appendChild(dots);

    slides.addEventListener(
      'scroll',
      () => {
        const i = Math.round(slides.scrollLeft / Math.max(1, slides.clientWidth));
        dots.querySelectorAll('span').forEach((d, n) => d.classList.toggle('is-active', n === i));
      },
      { passive: true }
    );
  };

  const open = async (btn, rawUrl, title) => {
    const url = new URL(rawUrl, location.origin);
    const dialog = document.getElementById('quick-add-dialog');
    const content = document.getElementById('quick-add-modal-content');

    btn.classList.add('is-loading');
    try {
      const QA = await getQuickAddClass();
      if (!QA || !dialog || !content || typeof dialog.showDialog !== 'function') {
        location.href = url.toString();
        return;
      }

      let grid = cache.get(url.pathname);
      if (!grid) {
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        grid = html.querySelector('[data-product-grid-content]');
        if (!grid) throw new Error('no product grid');
        cache.set(url.pathname, grid);
      }

      // A detached quick-add component does the modal assembly exactly the way
      // the theme does it for its own cards.
      const qa = document.createElement('quick-add-component');
      qa.dataset.productTitle = title || '';
      Object.defineProperty(qa, 'productPageUrl', { get: () => url.toString() });

      await qa.updateQuickAddModal(grid.cloneNode(true));
      const picker = content.querySelector('variant-picker');
      if (picker && typeof picker.updateVariantPicker === 'function') picker.updateVariantPicker(grid);

      addExtras(content, url.toString());
      dialog.showDialog();

      if (window.clarity) window.clarity('event', 'quickview_open');
    } catch (e) {
      location.href = url.toString();
    } finally {
      btn.classList.remove('is-loading');
    }
  };

  const TRIGGERS = [
    '.cpqv-btn',
    'product-card .cpcard-cta:not(.cpcard-cta--off)',
    '.cprail-card .cpcard-cta:not(.cpcard-cta--off)',
    '.cpc-up__btn--ghost',
  ].join(', ');

  /** Works out which product a trigger belongs to. */
  const targetFor = (trigger) => {
    if (trigger.matches('.cpqv-btn')) {
      return { url: trigger.dataset.url, title: trigger.dataset.title };
    }
    if (trigger.matches('.cpc-up__btn--ghost')) {
      const card = trigger.closest('.cpc-up__card');
      return {
        url: trigger.getAttribute('href'),
        title: (card?.querySelector('.cpc-up__name')?.textContent || '').trim(),
      };
    }
    const card = trigger.closest(CARD_SELECTOR);
    const link = card && productLink(card);
    return link ? { url: link.href, title: cardTitle(card) } : null;
  };

  // Window capture phase, so this runs before the whole-card click handler
  // (cp-ux-fixes.js), the AJAX add in cp-card-add.js and the card's own link.
  window.addEventListener(
    'click',
    (event) => {
      const trigger = event.target instanceof Element ? event.target.closest(TRIGGERS) : null;
      if (!trigger) return;
      const target = targetFor(trigger);
      if (!target || !target.url) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      open(trigger, target.url, target.title);
    },
    true
  );

  let timer = 0;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(addButtons, 150);
  };

  const start = () => {
    addButtons();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
